import React from 'react';

function InvestmentsDigitalAssets() {
  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative h-[56vh] min-h-[420px] overflow-hidden">
        <img src="/new-assets/pexels-karola-g-5717765.jpg" alt="Digital Assets" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/60 to-black/80" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-white text-4xl sm:text-5xl md:text-6xl font-light tracking-[-0.02em]">Digital Assets</h1>
            <p className="mt-4 text-white/90 max-w-2xl text-lg">Cryptocurrency, blockchain infrastructure, DeFi protocols and tokenized securities.</p>
          </div>
        </div>
      </section>

      {/* Investment Thesis */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-gray-900">Our Digital Assets Strategy</h2>
            <p className="mt-3 text-gray-600 max-w-3xl mx-auto">We invest across the digital asset ecosystem, from infrastructure to institutional-grade custody and decentralized finance protocols.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Core Investment Areas</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex gap-3"><span className="w-2 h-2 mt-2 bg-[#0056d7] rounded-full flex-shrink-0"/>Layer-1 and Layer-2 blockchain networks with proven scalability</li>
                <li className="flex gap-3"><span className="w-2 h-2 mt-2 bg-[#0056d7] rounded-full flex-shrink-0"/>Institutional custody, security and multi-signature wallet solutions</li>
                <li className="flex gap-3"><span className="w-2 h-2 mt-2 bg-[#0056d7] rounded-full flex-shrink-0"/>Decentralized exchanges, automated market makers and liquidity protocols</li>
                <li className="flex gap-3"><span className="w-2 h-2 mt-2 bg-[#0056d7] rounded-full flex-shrink-0"/>Staking infrastructure, validator operations and PoS networks</li>
                <li className="flex gap-3"><span className="w-2 h-2 mt-2 bg-[#0056d7] rounded-full flex-shrink-0"/>Tokenized real-world assets: real estate, commodities and securities</li>
                <li className="flex gap-3"><span className="w-2 h-2 mt-2 bg-[#0056d7] rounded-full flex-shrink-0"/>Compliant yield products, lending protocols and structured products</li>
              </ul>
            </div>
            <div className="relative">
              <img src="/images/about-us/cryptocurrency-3423267_640.jpg" alt="Digital Assets Strategy" className="w-full h-96 object-cover rounded-2xl shadow-xl" />
              <div className="absolute -bottom-5 -right-5 w-40 h-40 bg-[#0056d7] opacity-10 rounded-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Key Focus Areas */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-semibold text-gray-900 text-center mb-10">Investment Focus</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {t:'Infrastructure', d:'Nodes, validators, data indexing, oracles and interoperability solutions for multi-chain ecosystems.', img:'/images/lines-5475657_640.jpg'},
              {t:'DeFi Protocols', d:'DEXs, lending/borrowing, yield aggregators, derivatives and risk management primitives.', img:'/new-assets/pexels-leeloothefirst-7887821.jpg'},
              {t:'Institutional Products', d:'Custody, prime brokerage, OTC desks, tax reporting and regulatory compliance platforms.', img:'/images/traders-profit-279315.jpg'}
            ].map(x => (
              <article key={x.t} className="rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-shadow duration-300">
                <img src={x.img} alt={x.t} className="w-full h-48 object-cover" />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{x.t}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{x.d}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Risk & Compliance */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <img src="/images/tablet-business-overlay.jpg" alt="Risk Management" className="w-full h-96 object-cover rounded-2xl shadow-xl" />
          <div>
            <h2 className="text-3xl font-semibold text-gray-900 mb-4">Risk Management & Compliance</h2>
            <p className="text-gray-600 leading-relaxed mb-6">Our digital asset investments prioritize regulatory clarity, robust security audits, and transparent on-chain governance. We conduct comprehensive due diligence including smart contract audits, team background checks, tokenomics modeling and legal structure review.</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {['Smart contract audits', 'Legal entity structure', 'Multi-sig custody', 'Regulatory mapping', 'Token vesting schedules', 'Insurance coverage'].map(item => (
                <div key={item} className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-gray-700">{item}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Market Outlook */}
      <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold">Market Outlook</h2>
            <p className="mt-3 text-white/80 max-w-3xl mx-auto">Institutional adoption, regulatory clarity and infrastructure maturation are driving the next wave of digital asset growth.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {title: 'Institutional Adoption', desc: 'Pension funds, endowments and family offices allocating 1-5% to digital assets.'},
              {title: 'Regulatory Frameworks', desc: 'Clear custody rules, ETF approvals and stablecoin regulations improving market confidence.'},
              {title: 'Real-World Assets', desc: 'Tokenization of treasuries, real estate and commodities bringing trillions on-chain.'}
            ].map(item => (
              <div key={item.title} className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-white/70 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Office Locations */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-gray-900">Our Offices</h2>
            <p className="mt-3 text-gray-600 max-w-3xl mx-auto">Connect with our team at our headquarters</p>
          </div>
          <div className="grid md:grid-cols-1 gap-8 max-w-2xl mx-auto">
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#0056d7] rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">United Kingdom</h3>
                  <p className="text-gray-600">Bureau, 90 Fetter Lane</p>
                  <p className="text-gray-600">London, United Kingdom, EC4A 1EN</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-semibold text-gray-900">Explore Digital Asset Opportunities</h3>
          <p className="mt-2 text-gray-600">Access our research, portfolio companies and investment frameworks.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href="/apply" className="px-6 py-3 rounded-md bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] text-black font-semibold hover:shadow-lg transition-shadow">Apply now</a>
            <a href="/contact" className="px-6 py-3 rounded-md border-2 border-gray-300 text-gray-700 hover:border-gray-400 transition-colors">Contact us</a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default InvestmentsDigitalAssets;


